/* eslint max-lines: 0 */
// FieldType = 0 / 21
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useField } from 'formik';

import utils from 'utils';
import actions from 'state/actions';
import selectors from 'state/selectors';

import { AIsuggestion, FieldGroup, FieldTriggerConfig } from 'models/response';
import { Q1Value } from 'models/documents';

import { InputWrapper, InputsContainer } from './styles';

import Icon, { IconNames } from 'components/Input/Icon';
import SuggestionBox from 'components/SuggestionBox';

import { UserDate, UserDecimalSep } from 'models/user';
import CommonButton from 'components/Buttons/CommonButton';

import { useDecimalUpdater } from './List';
import { useExecuteActionQ1 } from 'utils/helpers/toolbar.helpers';

import { useInputQ1Logic } from './hooks/useInputQ1Logic';
import { InputFactory } from './InputFactory';
import userHelper from 'utils/helpers/user.helper';

interface Props {
  label: string;
  name: string;
  readonly?: boolean;
  hasQ3?: boolean;
  fieldGroup?: FieldGroup | null;
  fieldInputType: number;
  protocol: number | null;
  idField: number;
  hidden?: boolean;
  dateFormat: UserDate;
  separator: UserDecimalSep;
  multi?: boolean;
  index?: number;
  removeAction?: () => void;
  addAction?: () => void;
  triggerConfig: FieldTriggerConfig | null;
}

const InputQ1 = (props: Props) => {
  const {
    name,
    label,
    readonly = false,
    hasQ3 = false,
    fieldInputType,
    protocol,
    idField,
    dateFormat,
    separator,
    fieldGroup,
    multi,
    index,
    removeAction,
    addAction,
    hidden = false,
    triggerConfig,
  } = props;
  useDecimalUpdater(name);
  const [field, , helpers] = useField<Q1Value | undefined>(name);
  const t = utils.intl.useTranslator();

  const {
    suggestions,
    setCompanyName,
    status,
    showSuggestionsBox,
    setShowSuggestionsBox,
    config,
    decimalScale,
    isQ3visible,
    defaultSuggestion,
    onIconClick,
    setActiveBox,
    autoViewInPage,
    disabledCheck,
  } = useInputQ1Logic({
    name,
    protocol,
    idField,
    readonly,
    hasQ3,
    fieldGroup,
    field,
    multi,
    index,
  });
  const dispatch = useDispatch();
  const records = useSelector(selectors.documents.selectAllActiveDocuments);

  const executeActionQ1 = useExecuteActionQ1(triggerConfig);

  useEffect(() => {
    setCompanyName(records.length ? userHelper.getCompany(records, 'companyName') : null);
  }, [records, setCompanyName]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    try {
      if (e.shiftKey && e.key === 'Tab' && hasQ3 && isQ3visible) {
        e.preventDefault();
        const lastQ3Cell = document.querySelectorAll<HTMLInputElement>('#q3-last-cell input');
        if (lastQ3Cell.length) lastQ3Cell[0].click();
        return;
      }
      if (e.key === 'Tab' && hasQ3 && isQ3visible) {
        e.preventDefault();
        const firstQ3Cell = document.querySelectorAll<HTMLInputElement>('#q3-first-cell input');
        if (firstQ3Cell.length) firstQ3Cell[0].click();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onSuggestionSelection = (suggestion: AIsuggestion) => {
    if (field.value) {
      const suggestionValues = { ...suggestion };
      const arrayValues = [...field.value.content];
      suggestionValues.status = 3;
      arrayValues[multi && (index || index === 0) ? index : 0] = suggestionValues;
      helpers.setValue({
        ...field.value,
        content: arrayValues,
      });
      setShowSuggestionsBox(false);
      dispatch(actions.documents.setActiveBox(null));
    }
  };

  const getIcon = () => {
    const isConfirmed = !!(hasQ3 && field.value?.body?.length) || field?.value?.content[index ?? 0].status === 3;
    const baseIconName = fieldInputType === 5 ? 'paragraph-field' : hasQ3 ? 'border-all' : 'exclamation-circle';

    // Map old icon names with confirmed attribute to new Icon names
    let iconName: IconNames = baseIconName;
    if (baseIconName === 'border-all') {
      iconName = isConfirmed ? 'table-verified' : 'table-not-verified';
    } else if (baseIconName === 'exclamation-circle') {
      iconName = !isConfirmed ? 'tooltip-filled' : 'exclamation-circle';
    } else if (baseIconName === 'paragraph-field') {
      iconName = isConfirmed ? 'paragraph-confirmed' : 'paragraph';
    }

    return <Icon disabled={disabledCheck} icon={iconName} onClick={() => !disabledCheck && onIconClick()} />;
  };

  const renderInput = (fieldInputType: number) => {
    if (!field.value) return null;

    const feedbackColor = utils.input.getInputStatusColor(idField, status, readonly);
    const feedbackMessage = utils.input.getInputStatusMessage(idField, status, readonly);
    const hasFeedback = Boolean(feedbackColor);

    const inputProps = {
      fieldInputType,
      field,
      helpers,
      feedbackColor,
      hasFeedback,
      feedbackMessage,
      autoViewInPage,
      onKeyDown,
      executeActionQ1,
      dispatch,
      config,
      decimalScale,
      multi,
      label,
      readonly,
      dateFormat,
      triggerConfig,
      name,
      index,
      separator,
      icon: getIcon(),
    };

    const inputElement = <InputFactory {...inputProps} />;
    return <>{inputElement}</>;
  };

  const handleAddAction = useCallback(() => {
    addAction?.();
    if (triggerConfig) executeActionQ1();
  }, [addAction, triggerConfig, executeActionQ1]);

  const handleRemoveAction = useCallback(() => {
    removeAction?.();
    if (triggerConfig) executeActionQ1();
  }, [removeAction, triggerConfig, executeActionQ1]);

  return field.value ? (
    <InputWrapper>
      <InputsContainer hidden={hidden} multi={multi}>
        {multi && removeAction && (
          <CommonButton
            id="a1-remove-input-btn"
            tooltip={t('add-input')}
            scope="secondary"
            action={handleRemoveAction}
            customIcon="minus"
            disabled={readonly}
          />
        )}
        {multi && addAction && (
          <CommonButton
            id="q1-add-input-btn"
            tooltip={t('add-input')}
            scope="secondary"
            action={handleAddAction}
            customIcon="plus"
            disabled={readonly}
          />
        )}
        {renderInput(fieldInputType)}
      </InputsContainer>
      {suggestions?.length && showSuggestionsBox && !hasQ3 && !hidden ? (
        <SuggestionBox
          defaultValue={defaultSuggestion}
          suggestions={suggestions}
          onSelection={onSuggestionSelection}
          setActiveBox={setActiveBox}
          marginLeft={multi ? '-124px' : '0px'}
        />
      ) : null}
    </InputWrapper>
  ) : null;
};

export default InputQ1;
